import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  ChartLine,
  Brain,
  Code,
  Database,
  ChartBar,
  Trophy,
  Clock,
  Fire,
  CheckCircle,
  Circle,
  CaretRight,
  CaretLeft,
  PaperPlaneTilt,
  SpeakerHigh,
  X,
  Lightning,
  ArrowLeft,
  GraduationCap,
  Link as LinkIcon,
  Play,
  User,
  Globe,
  Briefcase,
  MonitorPlay
} from '@phosphor-icons/react';
import CodePlayground from './CodePlayground';
import ResourcesTab from './ResourcesTab';
import { DATA_SCIENCE_FINANCE_COURSE } from './courseDataRich';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stage icons mapping
const stageIcons = {
  1: Code,
  2: Database,
  3: ChartLine,
  4: ChartBar,
  5: BookOpen,
  6: Brain,
  7: Globe,
  8: Briefcase
};

// Floating Orb Component
const FloatingOrb = ({ onClick, isSpeaking, isOpen }) => (
  <motion.button
    data-testid="orb-tutor-trigger"
    onClick={onClick}
    className={`fixed bottom-6 right-6 w-16 h-16 rounded-full cursor-pointer z-50 ${
      isSpeaking ? 'orb-speaking' : 'orb-idle'
    }`}
    style={{
      background: 'radial-gradient(circle at 30% 30%, #fdba74, #fb923c, #ea580c)',
    }}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
  >
    {isOpen && (
      <X size={24} weight="bold" className="text-zinc-900 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
    )}
  </motion.button>
);

// Chat Panel Component
const ChatPanel = ({ isOpen, messages, onSendMessage, isLoading, isSpeaking, onSpeak }) => {
  const [input, setInput] = useState('');
  const messagesEndRef = React.useRef(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      onSendMessage(input.trim());
      setInput('');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          data-testid="chat-panel"
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-48px)] h-[500px] bg-zinc-900 border border-white/10 rounded-lg shadow-2xl z-40 flex flex-col overflow-hidden"
        >
          <div className="p-4 border-b border-white/10 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-300 to-orange-500" />
            <div>
              <h3 className="font-semibold text-white font-outfit">AI Tutor</h3>
              <p className="text-xs text-zinc-400">Powered by Claude</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center text-zinc-500 mt-8">
                <p className="text-sm">Hi! I'm your AI tutor.</p>
                <p className="text-sm mt-2">Ask me anything about data science & finance!</p>
              </div>
            )}
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  msg.role === 'user' ? 'bg-orange-500/20 text-orange-100' : 'bg-zinc-800 text-zinc-100'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === 'assistant' && (
                    <button onClick={() => onSpeak(msg.content)} className="mt-2 text-xs text-zinc-400 hover:text-orange-400 flex items-center gap-1">
                      <SpeakerHigh size={14} /> Speak
                    </button>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-zinc-800 p-3 rounded-lg flex gap-1">
                  <span className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="p-4 border-t border-white/10">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask your tutor..."
                className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-4 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500/50"
                disabled={isLoading}
              />
              <button type="submit" disabled={isLoading || !input.trim()} className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white p-2 rounded-lg">
                <PaperPlaneTilt size={20} weight="fill" />
              </button>
            </div>
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Lesson Content View Component
const LessonView = ({ lesson, onBack, onAskTutor }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    className="bg-zinc-900/50 border border-white/10 rounded-lg overflow-hidden"
  >
    {/* Lesson Header */}
    <div className="p-6 border-b border-white/10">
      <button onClick={onBack} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors">
        <ArrowLeft size={18} />
        <span className="text-sm">Back to Stage</span>
      </button>
      <div className="flex items-start gap-4">
        <div className="text-4xl">{lesson.icon}</div>
        <div>
          <h2 className="text-2xl font-outfit font-bold text-white">{lesson.title}</h2>
          <p className="text-zinc-400 mt-1">{lesson.desc}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {lesson.tags.map((tag, i) => (
              <span key={i} className="text-xs px-2 py-1 bg-orange-500/10 text-orange-400 rounded">{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Lesson Content */}
    <div className="p-6 space-y-6">
      {/* Overview */}
      <div>
        <h3 className="text-lg font-outfit font-semibold text-white mb-3 flex items-center gap-2">
          <BookOpen size={20} className="text-orange-400" />
          Overview
        </h3>
        <p className="text-zinc-300 leading-relaxed">{lesson.content.overview}</p>
      </div>

      {/* Key Concepts */}
      <div>
        <h3 className="text-lg font-outfit font-semibold text-white mb-3 flex items-center gap-2">
          <Brain size={20} className="text-orange-400" />
          Key Concepts
        </h3>
        <ul className="space-y-2">
          {lesson.content.keyConceptsList.map((concept, i) => (
            <li key={i} className="flex items-start gap-3 text-zinc-300">
              <span className="text-orange-400 mt-1">•</span>
              <span>{concept}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Code Example */}
      <div>
        <h3 className="text-lg font-outfit font-semibold text-white mb-3 flex items-center gap-2">
          <Code size={20} className="text-orange-400" />
          Code Example
        </h3>
        <div className="bg-zinc-950 border border-white/10 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-zinc-900/50">
            <span className="text-xs text-zinc-500 font-mono">Python</span>
            <button 
              onClick={() => navigator.clipboard.writeText(lesson.content.codeExample)}
              className="text-xs text-zinc-400 hover:text-white transition-colors"
            >
              Copy
            </button>
          </div>
          <pre className="p-4 overflow-x-auto text-sm">
            <code className="text-green-400 font-mono whitespace-pre">{lesson.content.codeExample}</code>
          </pre>
        </div>
      </div>

      {/* Finance Application */}
      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
        <h3 className="text-lg font-outfit font-semibold text-orange-400 mb-2 flex items-center gap-2">
          <ChartLine size={20} />
          Finance Application
        </h3>
        <p className="text-orange-100">{lesson.content.financeApplication}</p>
      </div>

      {/* Key Takeaways */}
      <div>
        <h3 className="text-lg font-outfit font-semibold text-white mb-3 flex items-center gap-2">
          <Trophy size={20} className="text-orange-400" />
          Key Takeaways
        </h3>
        <div className="grid md:grid-cols-2 gap-3">
          {lesson.content.keyTakeaways.map((takeaway, i) => (
            <div key={i} className="bg-zinc-800/50 rounded-lg p-3 flex items-start gap-2">
              <CheckCircle size={18} className="text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-zinc-300">{takeaway}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Resources */}
      {lesson.content.resources && lesson.content.resources.length > 0 && (
        <div>
          <h3 className="text-lg font-outfit font-semibold text-white mb-3 flex items-center gap-2">
            <LinkIcon size={20} className="text-orange-400" />
            Resources
          </h3>
          <div className="space-y-2">
            {lesson.content.resources.map((resource, i) => (
              <a 
                key={i} 
                href={resource.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors group"
              >
                <span className="text-xs px-2 py-1 bg-zinc-700 rounded text-zinc-400 uppercase">{resource.type}</span>
                <span className="text-zinc-300 group-hover:text-white">{resource.title}</span>
                <CaretRight size={16} className="text-zinc-500 ml-auto" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Ask Tutor CTA */}
      <div className="border-t border-white/10 pt-6">
        <button 
          onClick={() => onAskTutor(`Help me understand: ${lesson.title}`)}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
        >
          <GraduationCap size={20} />
          Ask AI Tutor About This Topic
        </button>
      </div>
    </div>
  </motion.div>
);

// Stage Card Component - Enhanced with clickable lessons
const StageCard = ({ stage, isExpanded, onToggle, onLessonClick }) => {
  const Icon = stageIcons[stage.id] || BookOpen;

  return (
    <motion.div
      data-testid={`roadmap-stage-${stage.id}`}
      className="stage-card bg-zinc-900/50 border border-white/10 rounded-lg overflow-hidden"
      layout
    >
      <button
        data-testid={`stage-header-${stage.id}`}
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-4 hover:bg-zinc-800/30 transition-colors"
      >
        <div 
          className="w-12 h-12 rounded-lg flex items-center justify-center"
          style={{ background: `${stage.color}20`, color: stage.color }}
        >
          <Icon size={24} weight="duotone" />
        </div>
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 font-medium">STAGE {stage.id}</span>
            <span className="text-xs text-zinc-600">•</span>
            <span className="text-xs text-zinc-500">{stage.duration}</span>
          </div>
          <h3 className="font-outfit font-semibold text-white mt-0.5">{stage.title}</h3>
          <p className="text-xs text-zinc-500 mt-1">{stage.focus}</p>
        </div>
        <div className="text-right mr-2">
          <div className="text-lg font-outfit font-bold" style={{ color: stage.color }}>{stage.lessons.length}</div>
          <div className="text-xs text-zinc-500">lessons</div>
        </div>
        <motion.div animate={{ rotate: isExpanded ? 90 : 0 }}>
          <CaretRight size={20} className="text-zinc-500" />
        </motion.div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-3">
              {/* Lessons */}
              {stage.lessons.map((lesson, idx) => (
                <button
                  key={idx}
                  onClick={() => onLessonClick(stage.id, idx)}
                  className="w-full flex items-start gap-3 p-3 bg-zinc-800/40 rounded-lg hover:bg-zinc-800/70 transition-colors text-left group"
                >
                  <span className="text-2xl">{lesson.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white text-sm group-hover:text-orange-400 transition-colors">{lesson.title}</div>
                    <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{lesson.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {lesson.tags.map((tag, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 bg-zinc-700/50 text-zinc-400 rounded">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <CaretRight size={18} className="text-zinc-500 group-hover:text-orange-400 mt-1" />
                </button>
              ))}

              {/* Practice Projects */}
              {stage.practiceProjects && stage.practiceProjects.length > 0 && (
                <div className="pt-3 border-t border-white/10">
                  <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Practice Projects</h4>
                  <ul className="space-y-1">
                    {stage.practiceProjects.map((project, idx) => (
                      <li key={idx} className="text-sm text-zinc-400 flex items-start gap-2">
                        <Lightning size={14} className="text-green-400 mt-1 flex-shrink-0" />
                        {project}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Certification */}
              {stage.certification && (
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Trophy size={16} className="text-orange-400" />
                    <span className="text-xs font-semibold text-orange-400 uppercase">Certification Milestone</span>
                  </div>
                  <p className="text-sm text-orange-200 mt-1">{stage.certification}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Master Class Card
const MasterClassCard = ({ insight }) => (
  <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-5">
    <div className="flex items-center gap-3 mb-3">
      <span className="text-3xl">{insight.icon}</span>
      <div>
        <div className="font-outfit font-semibold text-white">{insight.name}</div>
        <div className="text-xs text-zinc-500">{insight.role}</div>
      </div>
    </div>
    <h3 className="font-outfit font-semibold text-orange-400 text-lg mb-2">{insight.title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed mb-4">{insight.text}</p>
    <ul className="space-y-2">
      {insight.principles.map((p, i) => (
        <li key={i} className="text-xs text-zinc-500 flex items-start gap-2">
          <span className="text-orange-400 mt-0.5">•</span>
          {p}
        </li>
      ))}
    </ul>
  </div>
);

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, subvalue, color = 'text-orange-400' }) => (
  <div className="bg-zinc-900/50 border border-white/10 rounded-lg p-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
        <Icon size={20} className={color} weight="duotone" />
      </div>
      <div>
        <p className="text-2xl font-outfit font-bold text-white">{value}</p>
        <p className="text-xs text-zinc-500">{label}</p>
        {subvalue && <p className="text-xs text-zinc-600">{subvalue}</p>}
      </div>
    </div>
  </div>
);

// Main Dashboard Component
const Dashboard = ({ onBack }) => {
  const [expandedStage, setExpandedStage] = useState(1);
  const [activeTab, setActiveTab] = useState('roadmap');
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioRef] = useState(() => new Audio());

  const course = DATA_SCIENCE_FINANCE_COURSE;

  // Handle chat message
  const handleSendMessage = useCallback(async (message) => {
    setChatMessages(prev => [...prev, { role: 'user', content: message }]);
    setChatLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, { message });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setChatLoading(false);
    }
  }, []);

  // Handle text-to-speech
  const handleSpeak = useCallback(async (text) => {
    if (isSpeaking) {
      audioRef.pause();
      setIsSpeaking(false);
      return;
    }

    try {
      setIsSpeaking(true);
      const res = await axios.post(`${API}/tts`, { text: text.slice(0, 4096), voice: 'nova' });
      audioRef.src = `data:audio/mp3;base64,${res.data.audio}`;
      audioRef.onended = () => setIsSpeaking(false);
      await audioRef.play();
    } catch (error) {
      setIsSpeaking(false);
    }
  }, [isSpeaking, audioRef]);

  // Handle lesson click
  const handleLessonClick = (stageId, lessonIdx) => {
    const stage = course.curriculum.find(s => s.id === stageId);
    if (stage && stage.lessons[lessonIdx]) {
      setSelectedLesson({ stageId, lessonIdx, lesson: stage.lessons[lessonIdx] });
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="data-science-finance-dashboard">
      <main className="p-4 md:p-6 lg:p-8 overflow-auto">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors" data-testid="back-to-courses">
              <CaretLeft size={20} />
              <span className="text-sm">Back to Courses</span>
            </button>
          )}

          {/* Header */}
          <header className="mb-8">
            <p className="text-xs text-orange-500 uppercase tracking-[0.2em] font-semibold">Personal Learning Roadmap</p>
            <h1 className="text-3xl lg:text-4xl font-outfit font-bold text-white mt-2">{course.title}</h1>
            <p className="text-zinc-400 mt-2">{course.subtitle} • {course.year}</p>
            <p className="text-sm text-zinc-500 mt-1">
              Goal: <span className="text-orange-400">{course.goal}</span> • {course.totalDuration} • {course.dailyCommitment}
            </p>
          </header>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatsCard icon={BookOpen} label="Stages" value={course.stats.stages} subvalue="learning stages" />
            <StatsCard icon={Lightning} label="Topics" value={course.stats.topics} subvalue="comprehensive lessons" />
            <StatsCard icon={Clock} label="Timeline" value={course.stats.monthsToMastery} subvalue="months to mastery" />
            <StatsCard icon={Trophy} label="Potential" value={course.stats.careerPotential} subvalue="career growth" />
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {[
              { id: 'roadmap', label: 'Curriculum', icon: BookOpen },
              { id: 'resources', label: 'Resources', icon: MonitorPlay },
              { id: 'practice', label: 'Practice', icon: Lightning, badge: 'New' },
              { id: 'masterclass', label: 'Master Class', icon: GraduationCap },
              { id: 'glossary', label: 'Glossary', icon: BookOpen }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedLesson(null); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
                {tab.badge && <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">{tab.badge}</span>}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'roadmap' && !selectedLesson && (
              <motion.section key="roadmap" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <div className="mb-6">
                  <span className="text-xs text-orange-500 uppercase tracking-widest font-semibold">The Full Curriculum</span>
                  <h2 className="text-2xl font-outfit font-bold text-white mt-1">Eight Stages to Mastery</h2>
                  <p className="text-sm text-zinc-400 mt-2">Click each stage to expand, then click a lesson to read the full content.</p>
                </div>
                <div className="space-y-3">
                  {course.curriculum.map(stage => (
                    <StageCard
                      key={stage.id}
                      stage={stage}
                      isExpanded={expandedStage === stage.id}
                      onToggle={() => setExpandedStage(expandedStage === stage.id ? null : stage.id)}
                      onLessonClick={handleLessonClick}
                    />
                  ))}
                </div>
              </motion.section>
            )}

            {activeTab === 'roadmap' && selectedLesson && (
              <LessonView
                key="lesson"
                lesson={selectedLesson.lesson}
                onBack={() => setSelectedLesson(null)}
                onAskTutor={(q) => { setChatOpen(true); handleSendMessage(q); }}
              />
            )}

            {activeTab === 'resources' && (
              <motion.section key="resources" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <ResourcesTab />
              </motion.section>
            )}

            {activeTab === 'practice' && (
              <motion.section key="practice" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <h2 className="text-xl font-outfit font-semibold text-white mb-4 flex items-center gap-2">
                  <Lightning size={24} className="text-green-400" />
                  Python Practice
                </h2>
                <p className="text-zinc-400 text-sm mb-6">Practice Python with finance-focused exercises. Run code directly in your browser!</p>
                <CodePlayground
                  currentStage={expandedStage || 1}
                  onAskTutor={(question) => { setChatOpen(true); handleSendMessage(question); }}
                />
              </motion.section>
            )}

            {activeTab === 'masterclass' && (
              <motion.section key="masterclass" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <div className="mb-6">
                  <span className="text-xs text-orange-500 uppercase tracking-widest font-semibold">{course.masterClass.title}</span>
                  <h2 className="text-2xl font-outfit font-bold text-white mt-1">{course.masterClass.subtitle}</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {course.masterClass.insights.map((insight, i) => (
                    <MasterClassCard key={i} insight={insight} />
                  ))}
                </div>
              </motion.section>
            )}

            {activeTab === 'glossary' && (
              <motion.section key="glossary" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <div className="mb-6">
                  <span className="text-xs text-orange-500 uppercase tracking-widest font-semibold">Key Terms</span>
                  <h2 className="text-2xl font-outfit font-bold text-white mt-1">Data Science & Finance Glossary</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {course.glossary.map((item, i) => (
                    <div key={i} className="bg-zinc-900/50 border border-white/10 rounded-lg p-4">
                      <div className="font-semibold text-orange-400 font-mono text-sm">{item.term}</div>
                      <p className="text-sm text-zinc-400 mt-1">{item.def}</p>
                    </div>
                  ))}
                </div>
              </motion.section>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Floating Orb & Chat */}
      <FloatingOrb onClick={() => setChatOpen(!chatOpen)} isSpeaking={isSpeaking} isOpen={chatOpen} />
      <ChatPanel
        isOpen={chatOpen}
        messages={chatMessages}
        onSendMessage={handleSendMessage}
        isLoading={chatLoading}
        isSpeaking={isSpeaking}
        onSpeak={handleSpeak}
      />
    </div>
  );
};

export default Dashboard;
